import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import type { Dispatch, SetStateAction } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useCookie } from 'react-use'
import type {
  Consent,
  CookieCategory,
  CookieDeclaration,
} from './cookieConsent'
import { cookieIgnoreList } from './cookieConsent'
import {
  categories,
  categoryDescription,
  categoryMap,
  categoryTitle,
  useCookieConsentStore,
  useCookieDeclarations,
  useHasCookieConsentSupport,
} from './cookieConsent'
import Debug from 'debug'
import { ToggleButton } from '@jotta/ui/ToggleButton'
import { Modal } from '@jotta/ui/Modal'
import SvgCaretDown from '@jotta/ui/SvgCaretDown'
import SvgCaretRight from '@jotta/ui/SvgCaretRight'
import SvgLogoMain from '@jotta/ui/SvgLogo_mainLight'
import { useMutation } from '@connectrpc/connect-query'
import { consent as consentDescriptor } from '@jotta/grpc-connect-openapi/esm/openapi/consent/v1/consent.v1-ConsentService_connectquery'
import { publicTransport } from '@jotta/grpc-connect-client/transport'
import { ConsentRequest } from '@jotta/grpc-connect-openapi/consent'
import { Trans } from '@lingui/macro'
import { Button } from '@jotta/ui/Button'
import { decodeLegacyConsentCookie } from './legacyCookieConsent'
import { cookies } from '@jotta/utils/cookie'

const debug = Debug('CookieConsentModal')

declare global {
  interface Window {
    _mtm:
      | {
          push: (event: any) => void
        }
      | undefined

    gtag: ((action: string, context: string, params: any) => void) | undefined
  }
}

function shouldTrigger({
  statisticalCookieConsent,
  marketingCookieConsent,
}: Consent) {
  return [statisticalCookieConsent, marketingCookieConsent].some(
    value => value !== undefined,
  )
}

function MainPage() {
  return (
    <>
      <p>
        <Trans>
          We use cookies to improve and keep all Jottacloud services secure,
          customize and measure marketing, content and communications.
        </Trans>
      </p>
      <p>
        <Trans>
          You can check and change which data we process at any time in the
          cookie settings and you can read more in-depth about this in our&nbsp;
          <a className="link" href="/cookies" target="_blank">
            cookie policy
          </a>
        </Trans>
      </p>
    </>
  )
}

function Category({
  category,
  cookies,
  consent,
  updateConsent,
}: {
  category: CookieCategory
  cookies: CookieDeclaration[]
  consent: boolean | undefined
  updateConsent: (category: CookieCategory, consented: boolean) => void
}) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div>
      <div className="grid grid-cols-[1fr,2fr,40px] items-start gap-4">
        <Button
          icon={expanded ? SvgCaretDown : SvgCaretRight}
          onClick={() => setExpanded(!expanded)}
          className="btn btn-ghost label-md flex items-center"
        >
          {categoryTitle(category)}
        </Button>
        <div>{categoryDescription(category)}</div>
        {category === 'necessary' ? (
          <div />
        ) : (
          <ToggleButton
            checked={consent}
            onCheckedChange={a => updateConsent(category, a)}
          />
        )}
      </div>

      {expanded && (
        <div className="ml-2 border-b border-light pb-2">
          {cookies.map(
            ({
              data_processor_name,
              name,
              domain,
              description,
              expiration,
              data_processor_privacy_policy,
            }) => (
              <dl
                key={name}
                className="mt-4 grid grid-cols-1 items-center gap-x-3 border-t border-light pt-4 md:grid-cols-[auto,1fr]"
              >
                <dt className="label-sm">
                  <Trans>Service</Trans>
                </dt>
                <dd className="label-md">{data_processor_name}</dd>
                <dt className="label-sm">
                  <Trans>Purpose</Trans>
                </dt>
                <dd>{description}</dd>
                <dt className="label-sm">
                  <Trans>Privacy Policy</Trans>
                </dt>
                <dd className="break-all">
                  <a className="link" href={data_processor_privacy_policy}>
                    {data_processor_privacy_policy}
                  </a>
                </dd>
                <dt className="label-sm">
                  <Trans>Name</Trans>
                </dt>
                <dd>{name}</dd>
                <dt className="label-sm">
                  <Trans>Expiration</Trans>
                </dt>
                <dd>{expiration}</dd>
                <dt className="label-sm">
                  <Trans>Vendor</Trans>
                </dt>
                <dd>{domain}</dd>
              </dl>
            ),
          )}
        </div>
      )}
    </div>
  )
}

function Settings({
  cookieDeclarations,
  consent,
  updateConsent,
}: {
  cookieDeclarations: CookieDeclaration[]
  consent: Consent | undefined
  updateConsent: (category: CookieCategory, consented: boolean) => void
}) {
  return (
    <div className="flex w-full flex-col gap-4">
      {categories.map(category => {
        const categoryCookies = cookieDeclarations.filter(
          c => c.category === category,
        )
        return categoryCookies.length ? (
          <Category
            key={category}
            category={category}
            cookies={categoryCookies}
            consent={consent && consent[categoryMap[category]]}
            updateConsent={updateConsent}
          />
        ) : undefined
      })}
    </div>
  )
}

function matomoPush(event: any) {
  try {
    debug('matomo event', event)
    window._mtm?.push(event)
  } catch (e) {
    reportError(e)
  }
}

function triggerDefaults() {
  // https://developers.google.com/tag-platform/security/guides/consent?consentmode=advanced#default-consent
  debug('gtag default')
  try {
    window.gtag?.call(window.gtag, 'consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    })
  } catch (e) {
    reportError(e)
  }
}

function triggerEvent(consent: Consent) {
  const {
    functionalCookieConsent,
    statisticalCookieConsent,
    marketingCookieConsent,
  } = consent

  if (functionalCookieConsent) {
    matomoPush({
      event: 'cookie_cat_functional',
    })
  }
  if (statisticalCookieConsent) {
    matomoPush({
      event: 'cookie_cat_statistic',
    })
  }
  if (marketingCookieConsent) {
    matomoPush({
      event: 'cookie_cat_marketing',
    })
  }
  matomoPush({
    event: 'cookie_consent',
    cookie_cat_functional: functionalCookieConsent,
    cookie_cat_statistic: statisticalCookieConsent,
    cookie_cat_marketing: marketingCookieConsent,
  })

  // https://developers.google.com/tag-platform/security/guides/consent?consentmode=advanced#update-consent
  const gtagParameters = {
    ad_storage: marketingCookieConsent ? 'granted' : 'denied',
    ad_user_data: marketingCookieConsent ? 'granted' : 'denied',
    ad_personalization: marketingCookieConsent ? 'granted' : 'denied',
    analytics_storage: statisticalCookieConsent ? 'granted' : 'denied',
  }
  debug('gtag update', gtagParameters)
  try {
    window.gtag?.call(window.gtag, 'consent', 'update', gtagParameters)
  } catch (e) {
    reportError(e)
  }
}

export const CookieConsentContext = createContext({
  cookieConsentNeeded: true,
  disableUnconsentedCookieDetection: false,
  requestCookieConsent: () => {},
})

export function CookieConsentModal({
  openState,
  disableBlur,
}: {
  openState?: [boolean, Dispatch<SetStateAction<boolean>>]
  disableBlur?: boolean
}) {
  const hasCookieConsentSupport = useHasCookieConsentSupport()
  const [open, setOpen] = openState || [false, undefined]
  const cookieDeclarations = useCookieDeclarations()
  const [consent, setConsent] = useCookieConsentStore()
  const [customizedConsent, setCustomizedConsent] = useState(consent)
  const [displaySettings, setDisplaySettings] = useState(false)
  const { cookieConsentNeeded, disableUnconsentedCookieDetection } =
    useContext(CookieConsentContext)

  useEffect(() => {
    if (hasCookieConsentSupport) {
      triggerDefaults()
    }
  }, [hasCookieConsentSupport])

  useEffect(() => {
    setCustomizedConsent(consent)
    if (consent && shouldTrigger(consent)) {
      triggerEvent(consent)
    }
  }, [consent])

  const { mutate, isPending } = useMutation(consentDescriptor, {
    throwOnError: false,
    transport: publicTransport,
    onSettled: (response, error, request) => {
      setDisplaySettings(false)
      if (setOpen) {
        setOpen(false)
      }
      const persistedConsent = response
        ? request
        : {
            // we encountered an error and don't want to bother the user anymore.
            // store a conservative "consent" locally
            url: request.url,
          }
      setConsent(new ConsentRequest(persistedConsent))
      if (error) {
        reportError(error)
      }
    },
  })
  const persistConsent = useCallback(
    (newConsent: Consent) => {
      mutate({
        ...newConsent,
        clientUserId: newConsent.clientUserId || crypto.randomUUID(),
        url: location.href,
      })
    },
    [mutate],
  )
  const updateCustomizedConsent = useCallback(
    (category: CookieCategory, consented: boolean) => {
      setCustomizedConsent(
        prevState =>
          new ConsentRequest({
            ...prevState,
            [categoryMap[category]]: consented,
          }),
      )
    },
    [setCustomizedConsent],
  )

  // report unrecognized and unconsented cookies
  useEffect(() => {
    if (cookieDeclarations.length && !disableUnconsentedCookieDetection) {
      const cookieNames = Object.keys(cookies.getAll())
      const unrecognizedCookies: string[] = []
      const unconsentedCookies: string[] = []
      for (const cookieName of cookieNames) {
        if (cookieIgnoreList.includes(cookieName)) {
          continue
        }
        const declaration = cookieDeclarations.find(
          ({ name, regex }) => name === cookieName || regex?.test(cookieName),
        )
        if (!declaration) {
          unrecognizedCookies.push(cookieName)
          continue
        }
        const category = declaration.category
        if (
          category !== 'necessary' &&
          (!consent || !consent[categoryMap[category]])
        ) {
          unconsentedCookies.push(cookieName)
        }
      }
      if (unrecognizedCookies.length) {
        reportError(`unrecognized cookies: ${unrecognizedCookies}`)
      }
      if (unconsentedCookies.length) {
        reportError(`unconsented cookies: ${unconsentedCookies}`)
      }
    }
  }, [cookieDeclarations])

  // migrate legacy consent
  const [legacyConsentCookie] = useCookie('CookieInformationConsent')
  useEffect(() => {
    if (!consent && legacyConsentCookie) {
      const legacyConsent = decodeLegacyConsentCookie(legacyConsentCookie)
      if (legacyConsent) {
        persistConsent(legacyConsent)
      }
    }
  }, [persistConsent, consent, legacyConsentCookie])

  const isOpen = openState
    ? open
    : cookieConsentNeeded && !consent && !legacyConsentCookie

  return (
    hasCookieConsentSupport && (
      <Modal
        open={isOpen}
        disableBlur={disableBlur}
        style={{ zIndex: '2147483647' }}
        onClose={setOpen ? () => setOpen(false) : undefined}
      >
        <div className="m-auto flex max-h-full w-screen max-w-xl flex-col bg-primary sm:max-h-[90vh]">
          <div className="flex flex-col items-start gap-6 overflow-y-auto p-8">
            <PlainBrandIcon
              icon={SvgLogoMain}
              className="shrink-0"
              height={32}
            />

            <header className="h2">
              <Trans>We use cookies</Trans>
            </header>
            {displaySettings ? (
              <Settings
                cookieDeclarations={cookieDeclarations}
                consent={customizedConsent}
                updateConsent={updateCustomizedConsent}
              />
            ) : (
              <MainPage />
            )}
          </div>

          <div className="flex w-full flex-col gap-4 p-8 sm:flex-row">
            <button
              className="btn btn-outline w-full sm:w-auto"
              disabled={isPending}
              onClick={() => setDisplaySettings(!displaySettings)}
            >
              {displaySettings ? (
                <Trans>Hide settings</Trans>
              ) : (
                <Trans>Change settings</Trans>
              )}
            </button>

            <div className="flex grow flex-col justify-end gap-4 sm:flex-row">
              {displaySettings ? (
                <Button
                  loading={isPending}
                  className="btn btn-primary w-full sm:w-auto"
                  onClick={() =>
                    persistConsent({
                      ...customizedConsent,
                    })
                  }
                >
                  <Trans>Save settings</Trans>
                </Button>
              ) : (
                <>
                  <button
                    className="btn btn-outline"
                    disabled={isPending}
                    onClick={() =>
                      persistConsent({
                        ...consent,
                        necessaryCookieConsent: true,
                        marketingCookieConsent: false,
                        statisticalCookieConsent: false,
                        functionalCookieConsent: false,
                      })
                    }
                  >
                    <Trans>Reject unnecessary</Trans>
                  </button>
                  <button
                    className="btn btn-outline"
                    disabled={isPending}
                    onClick={() =>
                      persistConsent({
                        ...consent,
                        marketingCookieConsent: true,
                        statisticalCookieConsent: true,
                        necessaryCookieConsent: true,
                        functionalCookieConsent: true,
                      })
                    }
                  >
                    <Trans>Accept all</Trans>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>
    )
  )
}
